import { useState, useRef, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import LetterCard from "./LetterCard";
import { cn } from "@/lib/utils";
import { sampleLetters } from "@/data/sample-letters";

const SWIPE_THRESHOLD = 50;
const DRAG_RESISTANCE = 0.4;

const LetterCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const windowWidthRef = useRef(typeof window !== 'undefined' ? window.innerWidth : 400);

  useEffect(() => {
    const updateWidth = () => {
      windowWidthRef.current = window.innerWidth;
    };
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const goToPrevious = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? sampleLetters.length - 1 : prev - 1));
  }, []);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev === sampleLetters.length - 1 ? 0 : prev + 1));
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    
    const currentX = e.touches[0].clientX;
    let diff = currentX - touchStartX.current;
    
    const isAtStart = activeIndex === 0 && diff > 0;
    const isAtEnd = activeIndex === sampleLetters.length - 1 && diff < 0;
    
    if (isAtStart || isAtEnd) {
      diff = diff * DRAG_RESISTANCE;
    }
    
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null) {
      setIsDragging(false);
      setDragOffset(0);
      return;
    }
    
    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset < 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
    
    touchStartX.current = null;
    setIsDragging(false);
    setDragOffset(0);
  };

  const baseTranslate = -activeIndex * 100;
  const dragPercent = (dragOffset / windowWidthRef.current) * 100;
  const totalTranslate = baseTranslate + dragPercent;

  return (
    <div 
      className="relative select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Cards container */}
      <div className="relative overflow-hidden mx-[-0.5rem] md:mx-0">
        <div
          className={cn(
            "flex",
            isDragging ? "transition-none" : "transition-transform duration-500 ease-out"
          )}
          style={{ transform: `translateX(${totalTranslate}%)` }}
        >
          {sampleLetters.map((letter, index) => (
            <div key={letter.city} className="w-full flex-shrink-0 px-2 md:px-4">
              <LetterCard
                city={letter.city}
                country={letter.country}
                excerpt={letter.excerpt}
                fullText={letter.fullText}
                isActive={index === activeIndex}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 md:gap-6 mt-6 md:mt-8">
        <Button
          variant="eunice-ghost"
          size="icon"
          onClick={goToPrevious}
          aria-label="Previous letter"
          className="min-h-[48px] min-w-[48px]"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        <div className="flex gap-2">
          {sampleLetters.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className="min-h-[24px] min-w-[24px] flex items-center justify-center"
              aria-label={`Go to letter ${index + 1}`}
            >
              <span
                className={cn(
                  "h-2.5 md:h-2 rounded-full transition-all duration-300 block",
                  index === activeIndex
                    ? "w-8 bg-primary"
                    : "w-2.5 md:w-2 bg-tan hover:bg-secondary"
                )}
              />
            </button>
          ))}
        </div>

        <Button
          variant="eunice-ghost"
          size="icon"
          onClick={goToNext}
          aria-label="Next letter"
          className="min-h-[48px] min-w-[48px]"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default LetterCarousel;
